/**
 * Berkas MD merek per akun (docs/rencana-perombakan.md, "Brand & Styles").
 *
 * Nama berkas tetap TIDAK bisa ditambah atau dikurangi. Yang bisa dibuat user
 * hanyalah STYLE: `carousel/<slug>.md` dan `video/<slug>.md`. User tidak
 * pernah melihat nama berkas sebagai cara utama mengatur — UI menampilkannya
 * sebagai kartu kategori — tapi path inilah kunci penyimpanannya.
 *
 * MD adalah sumber kebenaran; template HTML adalah hasil kompilasinya.
 */

export type BrandFilePath = 'BRAND.md' | 'POSTER.md' | 'CAROUSEL.md' | 'VIDEO.md' | 'WRITING.md'
export type StyleKind = 'carousel' | 'video'

export interface BrandFileInfo {
  path: BrandFilePath
  title: string
  description: string
  /** Jenis style yang bernaung di bawah berkas ini. */
  styles?: StyleKind
}

export const BRAND_FILES: readonly BrandFileInfo[] = [
  { path: 'BRAND.md', title: 'Brand Identity', description: 'Who the account is, its audience, colours, and visual direction.' },
  { path: 'POSTER.md', title: 'Poster Design', description: 'How single-image posts look.' },
  { path: 'CAROUSEL.md', title: 'Carousel Design', description: 'Rules every carousel follows, whatever its style.', styles: 'carousel' },
  { path: 'VIDEO.md', title: 'Video Design', description: 'Rules every video follows, whatever its style: scenes, motion, and sound.', styles: 'video' },
  { path: 'WRITING.md', title: 'Writing Style', description: 'Voice, tone, and language for captions and on-screen text.' },
]

export const FIXED_PATHS = BRAND_FILES.map((f) => f.path) as readonly string[]

const SLUG = /^[a-z0-9]+(?:-[a-z0-9]+)*$/

/** "Versus / Comparison!" -> "versus-comparison" */
export function slugify(name: string): string {
  return name
    .toLowerCase()
    .normalize('NFKD')
    .replace(/[̀-ͯ]/g, '')
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 40)
}

export function stylePath(kind: StyleKind, slug: string): string {
  return `${kind}/${slug}.md`
}

/** Path yang sah: berkas tetap, atau `carousel|video/<slug>.md`. */
export function parseBrandPath(path: string): { fixed: BrandFilePath } | { style: StyleKind; slug: string } | null {
  if (FIXED_PATHS.includes(path)) return { fixed: path as BrandFilePath }
  const m = /^(carousel|video)\/([^/]+)\.md$/.exec(path)
  if (m && SLUG.test(m[2]!)) return { style: m[1] as StyleKind, slug: m[2]! }
  return null
}

/**
 * Isi awal. Sengaja umum dan pendek: akun langsung bisa memproduksi tanpa AI,
 * dan "Customize with AI" punya kerangka yang jelas untuk diisi.
 */
export const STARTER: Record<BrandFilePath, string> = {
  'BRAND.md': `# Brand

## Who we are
A news and insight account that explains what happened and why it matters, in plain words.

## Audience
Curious people who want the short version first.

## Colours
- Background: #F4EFE6 (light) or #111318 (dark)
- Text: #1C1B19 on light, #F5F3EE on dark
- Accent: #C2410C

## Typography
- Headlines: Playfair Display or Bebas Neue
- Body: Inter

## Visual direction
Clean, editorial, plenty of space. One idea per frame.
`,
  'POSTER.md': `# Poster

- One headline, at most two short sentences of body text.
- Use the article photo when there is one; otherwise a solid background.
- Show the source and the account handle at the bottom.
- The headline is the largest thing on the poster.
`,
  'CAROUSEL.md': `# Carousel

- The first slide is a hook: a short, bold title that makes people swipe.
- One idea per slide. Keep each slide under 40 words.
- Show the slide number (e.g. 2/6) and the account handle.
- The last slide asks people to follow or save.
`,
  'VIDEO.md': `# Video

- Hook in the first two seconds.
- Short scenes, one idea each, with large on-screen text.
- End with a clear call to follow.

## Motion

- Calm transitions: fades and short slides, no spinning.
- Text appears line by line, synced to the voice-over.
- Soft background music under the voice-over; short sound effects only on transitions.
`,
  'WRITING.md': `# Writing

- Write in the language of the source article unless told otherwise.
- Plain, direct sentences. No hype, no clickbait.
- Lead with what happened, then why it matters.
- Always name the source.
`,
}

/** Isi awal style baru tanpa AI: deskripsi user dibungkus kerangka. */
export function starterStyle(kind: StyleKind, name: string, description: string): string {
  return `# ${name}

${description.trim() || `A ${kind} style.`}

## Layout
- (Describe how slides or scenes in this style are arranged.)
`
}
